<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {BlogPostLayout, Hero} from '@react-spectrum/docs';
export default BlogPostLayout;

---
description: After a small break, we are back with another release. This one is jam packed with fixes and new features. We have improved Picker, Tooltip, ComboBox and updated a number of our aria hooks. Also check out our new components currently under construction including a Color package. A special thanks to all of our contributors for a very active new year.
date: 2021-02-17
---

# February 17, 2021 Release

After a small break, we are back with another release. This one is jam packed with fixes and new features. We have improved `Picker`, `Tooltip`, `ComboBox` and updated a number of our aria hooks. Also check out our new components currently under construction including a `Color` package. A special thanks to all of our contributors for a very active new year.

## New features
- Add `useUnwrapDOMRef` hook - [@tomsontom](https://github.com/tomsontom) - [PR](https://github.com/adobe/react-spectrum/pull/1322)
- Add autofocus to `Picker` - [@tomsontom](https://github.com/tomsontom) - [PR](https://github.com/adobe/react-spectrum/pull/1250)
- Add `order` to `styleProps` - [@renancleyson-dev](https://github.com/renancleyson-dev) - [PR](https://github.com/adobe/react-spectrum/pull/1351)
- Update component scaffolding - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/1364)
- Keep `Tooltip` open when hovered - [@Andarist](https://github.com/Andarist) - [PR](https://github.com/adobe/react-spectrum/pull/1377)
- Support left/right key in `useSelect` - [@tomsontom](https://github.com/tomsontom) - [PR](https://github.com/adobe/react-spectrum/pull/1299)
- Add filtering to `useAsyncList` hook - [@LFDanLu](https://github.com/LFDanLu) - [Docs](https://react-spectrum.adobe.com/react-stately/useAsyncList.html#filtering)
- Async support for `ComboBox` - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/1335) - [Docs](https://react-spectrum.adobe.com/react-spectrum/ComboBox.html#asynchronous-loading)
- Update validation icon position in `TextArea` - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1543)


## Fixed
- Add resize observing to `Picker` after initial render - [@tomsontom](https://github.com/tomsontom) - [PR](https://github.com/adobe/react-spectrum/pull/1284)
- Add `BreadcrumbItem` type interface - [@stefanprobst](https://github.com/stefanprobst) - [PR](https://github.com/adobe/react-spectrum/pull/1317)
- Update translation in `Picker` - [@RafalFilipek](https://github.com/RafalFilipek) - [PR](https://github.com/adobe/react-spectrum/pull/1309)
- Add resize observing to `ComboBox` - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1333)
- Use `documentElement` instead for `useInteractOutside`  - [@stefanprobst](https://github.com/stefanprobst) - [PR](https://github.com/adobe/react-spectrum/pull/1369)
- Update types for `Tooltip` hooks - [@Andarist](https://github.com/Andarist) - [PR](https://github.com/adobe/react-spectrum/pull/1372)
- Use `aria-readonly` in `CheckboxGroup` and `Radio` - [@intergalacticspacehighway](https://github.com/intergalacticspacehighway) - [PR](https://github.com/adobe/react-spectrum/pull/1414)
- Update required type for `CheckboxGroup` - [@stefanprobst](https://github.com/stefanprobst) - [PR](https://github.com/adobe/react-spectrum/pull/1337)
- Update `Button` type definition - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1361)
- Use `aria-readonly` in `RadioGroup` - [@intergalacticspacehighway](https://github.com/intergalacticspacehighway) - [PR](https://github.com/adobe/react-spectrum/pull/1424)
- Clean up deprecated test usage - [@matamatanot](https://github.com/matamatanot) - [PR](https://github.com/adobe/react-spectrum/pull/1430)
- Adjusted width of `Tooltip` - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/1451)
- Prevent extra `onChange` calls from `useControlledState` - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1480)
- Update flex styles on `Tabs` - [@tomsontom](https://github.com/tomsontom) - [PR](https://github.com/adobe/react-spectrum/pull/1473)
- Update `usePreventScroll` for SSR - [@dferber90](https://github.com/dferber90) - [PR](https://github.com/adobe/react-spectrum/pull/1315)
- Remove autocorrect from `ComboBox` - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/1502)
- Fix label css to grow with component height  - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/1575)
- Update `ComboBox` design - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/1492)
- Fix `TextArea` when inside a flex - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1531)
- Retain `ComboBox` selected key `onBlur` if value matches - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/1541)
- Prevent left/right arrow keys from scrolling containers in `Picker` - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1540)
- Add check for ref in `useSelectableList` - [@AndyOGo](https://github.com/AndyOGo) - [PR](https://github.com/adobe/react-spectrum/pull/1550)


## Docs
- Fix typo in contribution guide - [@stefanprobst](https://github.com/stefanprobst) - [PR](https://github.com/adobe/react-spectrum/pull/1319)
- Add the default for `level` prop in `Heading`  - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1349)
- Add example of passing a ref to `useButton` - [@toshi1127](https://github.com/toshi1127) - [PR](https://github.com/adobe/react-spectrum/pull/1298)
- Improve `useCheckboxGroupItem` examples - [@stefanprobst](https://github.com/stefanprobst) - [PR](https://github.com/adobe/react-spectrum/pull/1336)
- Update links for `TextArea` guidelines - [@matthewdeutsch](https://github.com/matthewdeutsch) - [PR](https://github.com/adobe/react-spectrum/pull/1357)
- Fix typo in `IllustratedMessage` example - [@travigd](https://github.com/travigd) - [PR](https://github.com/adobe/react-spectrum/pull/1410)
- Update example in `useRadio` to include a ref - [@matamatanot](https://github.com/matamatanot) - [PR](https://github.com/adobe/react-spectrum/pull/1370)
- Add anatomy diagrams to aria hooks - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/1452)
- Update prop description in `useMenuSection` - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/1461)
- Fix error in storybook - [@tomsontom](https://github.com/tomsontom) - [PR](https://github.com/adobe/react-spectrum/pull/1491)
- Add `useComboBox` hook documentation to react-aria - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/1276)


## Under construction

Pre-release versions of the following components have been released. Please feel free to try them out, and report any issues you encounter.

- NumberField - [React Spectrum](https://react-spectrum.adobe.com/react-spectrum/NumberField.html), [React Aria](https://react-spectrum.adobe.com/react-aria/useNumberField.html)
- ColorWheel - [React Aria](https://react-spectrum.adobe.com/react-aria/useColorWheel.html)
- ColorSlider - [React Aria](https://react-spectrum.adobe.com/react-aria/useColorSlider.html)
- ColorField - [React Aria](https://react-spectrum.adobe.com/react-aria/useColorField.html)
- ComboBox - [React Spectrum](https://react-spectrum.adobe.com/react-spectrum/ComboBox.html), [React Aria](https://react-spectrum.adobe.com/react-aria/useComboBox.html)

## Released packages
```
- @adobe/react-spectrum@3.8.0
- @internationalized/message@3.0.0-alpha.0
- @internationalized/number@3.0.0-alpha.0
- @react-aria/button@3.3.1
- @react-aria/color@3.0.0-alpha.0
- @react-aria/combobox@3.0.0-alpha.1
- @react-aria/i18n@3.3.0
- @react-aria/interactions@3.3.3
- @react-aria/listbox@3.2.4
- @react-aria/menu@3.1.4
- @react-aria/numberfield@3.0.0-alpha.0
- @react-aria/overlays@3.6.1
- @react-aria/radio@3.1.3
- @react-aria/select@3.3.0
- @react-aria/selection@3.3.2
- @react-aria/slider@3.0.1
- @react-aria/spinbutton@3.0.0-alpha.0
- @react-aria/tabs@3.0.0-alpha.3
- @react-aria/toggle@3.1.2
- @react-aria/tooltip@3.1.1
- @react-aria/utils@3.6.0
- @react-spectrum/button@3.4.0
- @react-spectrum/checkbox@3.2.2
- @react-spectrum/color@3.0.0-alpha.0
- @react-spectrum/combobox@3.0.0-alpha.1
- @react-spectrum/label@3.3.1
- @react-spectrum/numberfield@3.0.0-alpha.0
- @react-spectrum/picker@3.2.2
- @react-spectrum/radio@3.1.2
- @react-spectrum/searchfield@3.1.3
- @react-spectrum/slider@3.0.1
- @react-spectrum/statuslight@3.2.0
- @react-spectrum/tabs@3.0.0-alpha.4
- @react-spectrum/textfield@3.1.5
- @react-spectrum/tooltip@3.1.1
- @react-spectrum/utils@3.5.1
- @react-stately/color@3.0.0-alpha.0
- @react-stately/combobox@3.0.0-alpha.1
- @react-stately/data@3.2.0
- @react-stately/numberfield@3.0.0-alpha.0
- @react-stately/radio@3.3.0
- @react-stately/slider@3.0.1
- @react-stately/tabs@3.0.0-alpha.1
- @react-stately/tooltip@3.0.2
- @react-stately/utils@3.2.0
- @react-types/button@3.3.1
- @react-types/checkbox@3.2.2
- @react-types/color@3.0.0-alpha.0
- @react-types/combobox@3.0.0-alpha.1
- @react-types/label@3.4.0
- @react-types/numberfield@3.0.0-alpha.0
- @react-types/select@3.2.0
- @react-types/shared@3.4.0
- @react-types/slider@3.0.1
- @react-types/text@3.1.2
- @react-types/tooltip@3.1.1
- @spectrum-icons/color@3.3.0
- react-aria@3.4.0
- react-stately@3.3.0
```
